import './App.scss';
import Header from './components/common/header/header';
import Footer from './components/common/footer/footer';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import React, { Suspense, startTransition, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import AuthGuard from './guards/auth.guard';
import NonAuthGuard from './guards/non-auth.guard';
import { useExtendSessionMutation } from './api-integration/public/common';
import { 
  fullPageLoader, updateAlertMessage, updateIsSessionExpired, updateLogin, 
  updateLogout, updateReloadPageAfterSessionExpired, updateUser, 
  setAuthChecking, updateBLCUserGroup, updateLibraryType, selectFullPageLoader, selectAuthSession
} from './api-integration/common.slice';
import { getTranslatedText, resolveSessionExpiredMessage } from './util/util';
import { useValidateAuthTokenMutation, useGetSessionStatusQuery } from './api-integration/secure/api.account';
import { normalizeAPIResponse } from './util/normalizeAPIResponse';
import { hydrateSessionLibraryType } from './util/library/library-type.util';
import usePageTranslation from './hooks/ui/usePageTranslation';
import useInitializePopovers from './components/common/bootstrap-component/initializePopovers';
import { useModalScrollReconciler } from './components/common/modal/use-modal-scroll-reconciler';
import ChatBot from './components/secure/chatbot';
import { classifyError } from './util/telemetry/error/error-classifier.util';
import { InitializationErrorScreen } from './components/common/error/initialize-error-screen';
// START-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen]
import { OfflineScreen } from './features/offline/components/offline-screen';
import { useOfflineNavigationGuard } from './features/offline/hooks/use-offline-navigation-guard';
// /END-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen]
import ErrorBoundary from './components/common/error/error-boundary';

const Login = React.lazy(() => import('./pages/public/login/login'));
const Forgotpwd = React.lazy(() => import('./pages/public/login/forgot-pwd'));
const ExternalGateway = React.lazy(() => import('./pages/public/external-gateway/external-gateway'));

const Secure = React.lazy(() => import('./pages/secure/secure'));
const Alert = React.lazy(() => import('./components/common/alert/alert').then((module) => ({ default: module.Alert })));
const UnsubscribeUser = React.lazy(() => import("./pages/public/unsubscribe-user/unsubscribe-user"))
const VanityPage = React.lazy(() => import('./pages/common/vanityPage'));
const EventLogin = React.lazy(() => import('./pages/public/login/login-event'));
const Libraries = React.lazy(() => import('./pages/secure/libraries'));
const AppHome = React.lazy(() => import('./pages/secure/app-home'));
const MyMessages = React.lazy(() => import('./pages/secure/my-messages/my-messages'));
const KioskRoutes = React.lazy(() => import('./kiosk/app/kiosk-routes'));
const Referral = React.lazy(() => import('./components/common/referral-mapping/referral-mapping').then(m => ({ default: m.Referral })));

const App = () => {
  const fullPageLoaderSelector = useAppSelector(selectFullPageLoader);
  const {
    blcUserGroup,
    isSessionExpired,
    reloadPageAfterSessionExpired,
    isRefreshTokenExpired,
    isAuthChecking,
    isLoggedIn,
  } = useAppSelector(selectAuthSession);
  const location = useLocation();
  //const [, forceUpdate] = useReducer(x => x + 1, 0);
  const PageTranslation = usePageTranslation();
  // START-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen]
  const { showOfflineScreen, retry: retryAfterOffline } = useOfflineNavigationGuard();
  // /END-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen]
  useInitializePopovers();
  useModalScrollReconciler();
  const dispatch = useAppDispatch();
  const [validateAuthToken] = useValidateAuthTokenMutation();
  const [isvisibleMentohaChatBot, setIsvisibleMentohaChatBot] = useState(false);
  const [initErrorClassification, setInitErrorClassification] = useState<'NETWORK_ERROR' | 'SERVER_ERROR' | 'UNKNOWN_ERROR' | null>(null);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME || 'BlueCallom';
    sessionStorage.removeItem('chunk_reload_attempted');
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    const isPublicOrAuthPath = 
      path === '/' ||
      path === '/login' || 
      path === '/forgotpwd' || 
      path === '/eventLogin' || 
      path.startsWith('/external/') || 
      path.startsWith('/unsubscribe/') || 
      path.startsWith('/referral/') || 
      path.startsWith('/kiosk');
      
    if (!isPublicOrAuthPath) {
      sessionStorage.setItem('redirectURL', path);
    }
  }, [location]);

  useEffect(() => {
    if(blcUserGroup) {
      setIsvisibleMentohaChatBot(blcUserGroup?.blcPortalManager === 1);
    }
  },[blcUserGroup]);

  useEffect(() => {
    if(isLoggedIn) {
      dispatch(updateReloadPageAfterSessionExpired(false));
      const intervalId = setInterval(() => {
        validateAuthToken({});
      }, 30 * 60 * 1000); // 30 minutes in milliseconds-30 * 60 * 1000

      // Clean up function to clear the interval when the component unmounts
      return () => clearInterval(intervalId);
    }
  }, [dispatch, validateAuthToken, isLoggedIn]);

  const isKioskRoute = location.pathname.toLowerCase().startsWith('/kiosk');
  const isExternalRoute = location.pathname.toLowerCase().startsWith('/external');
  const isForgotPwdRoute = location.pathname.toLowerCase().startsWith('/forgotpwd');
  const isEventLoginRoute = location.pathname.toLowerCase().startsWith('/eventlogin');
  const shouldSkipSessionQuery = isKioskRoute || isExternalRoute || isForgotPwdRoute || isEventLoginRoute;
  
  const getSessionStatusResult = useGetSessionStatusQuery({ isMainApp: true }, { skip: shouldSkipSessionQuery });

  useEffect(() => {
    if (!shouldSkipSessionQuery) {
      dispatch(setAuthChecking(true));
    } else {
      dispatch(setAuthChecking(false));
    }
  }, [shouldSkipSessionQuery, dispatch]);

  useEffect(() => {
    if(getSessionStatusResult.isSuccess || getSessionStatusResult.isError) {
      const response = normalizeAPIResponse(getSessionStatusResult);
      if(response?.success === true && response?.data?.user) {
        const localUser = {
          userEmail: response?.data?.user?.email,
          userBusinessEmail: response?.data?.user?.businessemail,
          userId: response?.data?.user?.userId,
          username: response?.data?.user?.username,
          userType: response?.data?.user?.userType,
          userlevel: response?.data?.user?.userlevel,
          firstName: response?.data?.user?.firstName,
          lastName: response?.data?.user?.lastName,
          picPath: response?.data?.user?.picPath,
          totalCCPoints: response?.data?.user?.totalCCPoints,
          userCCBalance: response?.data?.user?.userCCBalance,
          orgCCBalance: response?.data?.user?.orgCCBalance,
          orgLowCoinAlert: response?.data?.user?.orgLowCoinAlert,
          usrCreatedDate: response?.data?.user?.usrCreatedDate,
          referralId: response?.data?.user?.referralId,
          blcFlag: response?.data?.user?.blcFlag,
          betaUser: response?.data?.user?.betaUser,
          accId: response?.data?.user?.accId,
          orgId: response?.data?.user?.orgId,
          accountType: response?.data?.user?.accountType,
          accountAssociation: response?.data?.user?.accountAssociation !== undefined && response?.data?.user?.accountAssociation !== null ? Number(response.data.user.accountAssociation) : undefined,
          showRefKey: response?.data?.user?.showRefKey,
          isNewUser: response?.data?.user?.isNewUser,
          showWelcomeNote: response?.data?.user?.isNewUser,
          isProfileComplete: response?.data?.user?.isProfileComplete,
          promptExeAlert: response?.data?.user?.preferences?.promptExeAlert,
          userLanguage: response?.data?.user?.preferences?.userLanguage,
          paginationRecordsPerPage: response?.data?.user?.preferences?.userPaginationRecordsPerPage,
          emailPrivacy: response?.data?.user?.preferences?.userPrivacy,
          sysUsageLevel: response?.data?.user?.sysUsageLevel,
          usrMessageAlertDuration: response?.data?.user?.preferences?.usrMessageAlertDuration,
          userPreferences: response?.data?.user?.preferences,
          userCompanyData: response?.data?.user?.companyData,
          userNotifications: response?.data?.user?.userNotifications,
          CCtoCHF: response?.data?.user?.CCtoCHF,
          userDeveloperAccess: response?.data?.user?.developerAccess,
          userRoles: response?.data?.user?.userRoles,
          userRights: response?.data?.user?.userRights,
          blcPremiumFee: response?.data?.user?.blcPremiumFee,
          visitedTeamPage: response?.data?.user?.visitedTeamPage                
        }
        dispatch(updateUser(localUser));
        dispatch(updateLogin());
        dispatch(updateLibraryType(hydrateSessionLibraryType(response?.data?.user?.preferences?.userLibraryDefault)));
        if (response.data.blcUserGroup) {
          dispatch(updateBLCUserGroup(response.data.blcUserGroup));
        }
        setInitErrorClassification(null);
      }
      else {
          if (getSessionStatusResult.isError) {
            const classification = classifyError(getSessionStatusResult.error);
            if (classification === 'NETWORK_ERROR' || classification === 'SERVER_ERROR' || classification === 'UNKNOWN_ERROR') {
              setInitErrorClassification(classification);
              startTransition(() => {
                dispatch(setAuthChecking(false));
              });
              return;
            }
          }
          dispatch(updateLogout());
      }
      startTransition(() => {
        dispatch(setAuthChecking(false));
      });
    }
  }, [getSessionStatusResult?.isSuccess, getSessionStatusResult?.isError]);

  const navigate = useNavigate();

  const [extendSesionAPI, { data: extendSessionResposne, isSuccess: isSessionExtended, isError: isErrorWhileExtendingSession, error: errorWhileExtendingSession }] =
    useExtendSessionMutation();

  // const [getProfileAPI, { data: profileData, isLoading: isProfileDataLoading, isSuccess: isGetProfileSuccess, isError: isProfileDataError, error: errorProfileData }] =
  //   useGetUserProfileMutation();

  const clearAndLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch(updateLogout());
    dispatch(updateIsSessionExpired(false));
    const translatedMsg = getTranslatedText('sessionExpired.message', PageTranslation);
    const alertMsg = resolveSessionExpiredMessage(translatedMsg);
    dispatch(updateAlertMessage({ status: 'error', message: alertMsg }));
    navigate('/login');
  }

  // const callProfileAPI = () => {
  //   dispatch(fullPageLoader(true));
  //   getProfileAPI({accountType: 'user'});
  // }

  useEffect(() => {
    // i18n.changeLanguage('fr');
    if (extendSessionResposne || isSessionExtended || isErrorWhileExtendingSession || errorWhileExtendingSession) {
      dispatch(fullPageLoader(false));
    }
    if (isErrorWhileExtendingSession) {
      clearAndLogout();
    }
  }, [extendSessionResposne, isSessionExtended, isErrorWhileExtendingSession]);

  useEffect(() => {
    if (isSessionExpired === true) {
      dispatch(fullPageLoader(true));
      extendSesionAPI({});
    }
  }, [isSessionExpired]);

  useEffect(() => {
    if (isRefreshTokenExpired) {
      clearAndLogout();
    }
  }, [isRefreshTokenExpired])

  useEffect(() => {
    if (isSessionExtended) {
      if (extendSessionResposne?.success === true) {
        dispatch(updateIsSessionExpired(false));
        //dispatch(updateAlertMessage({ status: 'success', message: t('message.session_extended') }));
        //dispatch(updateAlertMessage({ status: 'success', message: "Session Extended....." }));
        
        if (reloadPageAfterSessionExpired) {
          window.location.reload();
        }
          
        // setRefresh({});
        // forceUpdate();
      } else {
        dispatch(updateAlertMessage({ status: 'success', message: "Session could not be extended" }));
        clearAndLogout();
      }
    }
  }, [isSessionExtended]);

  useEffect(() => {
    const handleBeforeInput = (e: InputEvent) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (target && target.classList.contains('form-control') &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      ) {
        // Block typing leading whitespace (first character only)
        if (target.selectionStart === 0 && /^\s$/.test(e.data ?? '')) {
          e.preventDefault();
        }
      }
    };
    document.addEventListener('beforeinput', handleBeforeInput, true);
    return () => {
      document.removeEventListener('beforeinput', handleBeforeInput, true);
    };
  }, []);  

  // useEffect(() => {
  //   if (isGetProfileSuccess) {
  //     if (profileData.success == true && profileData.statusCode == 200) {
  //       dispatch(updateUser(profileData?.user));
  //       dispatch(fullPageLoader(false));
  //     } else {
  //       clearAndLogout();
  //       dispatch(updateAlertMessage({ status: 'success', message: profileData?.message }));
  //     }
  //   }
  // }, [isGetProfileSuccess]);

  useEffect(() => {
    const referrlKey = localStorage.getItem('refKey');
    if(!referrlKey) {
      // localStorage.clear();
    }
  }, []);
  const imageUrl = `${window.location.protocol}//${window.location.host}/bluePrompt.svg`;
  const fullUrl = `${window.location.protocol}//${window.location.host}${location.pathname}`;

  if (initErrorClassification) {
    return (
      <InitializationErrorScreen 
        classification={initErrorClassification} 
      />
    );
  }

  return (
    <>
      {(isAuthChecking && !isKioskRoute) && (
        <div className='fullpage-loader'>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      )}
        <meta property="og:url" content={fullUrl} />
        <meta property="og:title" content="Agentic AI Business Solutions" />
        <meta property="og:description" content="BlueCallom's Agentic AI Business Solutions, are elevating organizations to achieve goals that have not been possible before." />
        <meta property="og:image" content={imageUrl} />
        
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={fullUrl} />
        <meta property="twitter:title" content="Agentic AI Business Solutions" />
        <meta property="twitter:description" content="BlueCallom's Agentic AI Business Solutions, are elevating organizations to achieve goals that have not been possible before." />
        <meta property="twitter:image" content={imageUrl} />
      
      {!isKioskRoute && <Header />}
      <Suspense fallback={<div className='text-center'>{getTranslatedText('loading.message', PageTranslation)}</div>}>
        {/* START-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen] */}
        {/*
          Offline takeover replaces the whole route subtree, so it is a ternary rather than
          another `&&` branch — the routes must not render behind it.
          Kiosk routes are excluded: they run as a standalone, self-contained flow and keep
          their own error handling, the same carve-out the Header/Footer branches above and
          below already make.
          The boundary wraps both branches, so a render error in OfflineScreen still has a
          fallback instead of unmounting the React root. `key` remounts it across the
          offline/routes swap: without it a caught error would stick after the connection
          returns, since the boundary never resets hasError on its own.
        */}
        <ErrorBoundary key={showOfflineScreen && !isKioskRoute ? 'offline' : 'routes'}>
        {showOfflineScreen && !isKioskRoute ? (
          <OfflineScreen onRetry={retryAfterOffline} />
        ) : (!isAuthChecking || isKioskRoute) && (
        /* /END-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen] */
        <Routes>
          <Route path="/login" element={<NonAuthGuard><Login /></NonAuthGuard>} ></Route>
          <Route path="/eventLogin" element={<NonAuthGuard><EventLogin /></NonAuthGuard>}></Route>
          <Route path="/forgotpwd" element={<NonAuthGuard><Forgotpwd /></NonAuthGuard>} ></Route>
          <Route path="/external/:id" element={<NonAuthGuard><ExternalGateway /></NonAuthGuard>} ></Route>
          <Route path="/home" element={<AuthGuard><AppHome /></AuthGuard>} ></Route>
          {/* <Route path="/app/io/:id" element={<AskGPT />}></Route>
          <Route path="/app/io" element={<AskGPT />}></Route> */}
          {/* <Route path="/app/embed/prompt/:id" element={<EmbedPrompt />} ></Route> */}
          <Route path="/app/*" element={<AuthGuard><Secure /></AuthGuard>} ></Route>
          <Route path="/referral/:id" element={<NonAuthGuard><Referral /></NonAuthGuard>} />
          <Route path="/company/:id" element={<AuthGuard><Libraries /></AuthGuard>}></Route>
          <Route path="/unsubscribe/:id" element={<UnsubscribeUser />}></Route>
          {/* <Route path="/app/chatMode" element={<SimpleChat />} ></Route> */}
          {/* <Route path="/cardBlue/:id" element={<CardBlueUserInfo />}></Route> */}
          <Route path="/"  element={<Navigate to="/login" replace />} ></Route>
          <Route path="/:id"  element={<AuthGuard><VanityPage /></AuthGuard>} ></Route>
          <Route path="/app/myMessages" element={<AuthGuard><MyMessages /></AuthGuard>} />
          <Route path="/kiosk/*" element={<KioskRoutes />} />
        </Routes>
        )}
        {/* START-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen] */}
        {/* Closing tag of the boundary opened above the offline/routes ternary. */}
        </ErrorBoundary>
        {/* /END-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen] */}
      </Suspense>
      {(!isKioskRoute &&
        isvisibleMentohaChatBot &&
        !['login', 'eventLogin', 'forgotpwd', 'external', 'unsubscribe'].some(route => location.pathname.includes(route))) && (
        <div className="container position-relative">
          <div className="d-flex justify-content-end align-items-end">
            <ChatBot />
          </div>
        </div>
      )}
      {!isKioskRoute && <Footer />}
      {fullPageLoaderSelector && 
        <div className='fullpage-loader' >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              Loading…
            </span>
          </div>
        </div>
      }
      <Alert />
    </>
  );
}

export default App;
